// Shared min/max range formatting for `model.specs_range` (auto-synced across all
// active variants of a model) and `model.mileage_range` — used by the key specs
// grids and the full spec/feature table.

export const MILEAGE_SPEC_KEY = "arai_mileage";
// Matches the actual spec key the variant edit form submits for EV range —
// not "claimed_range", which isn't wired to the real admin form.
export const RANGE_SPEC_KEY = "range";

const POWER_TORQUE_KEYS = new Set(["max_power", "max_torque"]);

/** Text specs — never show as numeric min/max ranges. */
export const TEXT_SPEC_KEYS = new Set([
  "gearbox",
  "transmission_type",
  "fuel_type",
  "engine_type",
  "motor_type",
]);

export interface ISpecRange {
  key: string;
  label?: string;
  unit?: string | null;
  min: number;
  max: number;
}

export function getSpecRangeByKey(
  specs: ISpecRange[] | undefined | null,
  key: string,
): ISpecRange | undefined {
  return specs?.find(
    (spec) => spec.key?.trim().toLowerCase() === key?.trim().toLowerCase(),
  );
}

export function formatSpecRange(
  min: number | null | undefined,
  max: number | null | undefined,
  unit?: string | null,
): string {
  const u = unit ?? "";
  if (min != null && max != null) {
    return min === max ? `${min} ${u}`.trim() : `${min} ${u} - ${max} ${u}`.trim();
  }
  if (min != null) return `${min} ${u}`.trim();
  if (max != null) return `${max} ${u}`.trim();
  return "";
}

function rangeUnitForKey(key: string, unit?: string | null): string {
  return POWER_TORQUE_KEYS.has(key.trim().toLowerCase())
    ? unit?.split("@")[0]?.trim() ?? ""
    : unit ?? "";
}

/** Formatted min/max for one spec key from `model.specs_range`. */
export function formatSpecsRangeValue(
  specsRange: ISpecRange[] | undefined | null,
  key: string,
): string {
  const normalized = key.trim().toLowerCase();
  if (TEXT_SPEC_KEYS.has(normalized)) return "";

  const range = getSpecRangeByKey(specsRange, key);
  if (!range || (range.min == null && range.max == null)) return "";

  return formatSpecRange(
    range.min,
    range.max,
    rangeUnitForKey(normalized, range.unit),
  );
}

/** Transmission labels only — excludes "Gearbox". */
export function isTransmissionSpecLabel(label?: string): boolean {
  if (!label) return false;
  const normalized = label.trim().toLowerCase();
  return !normalized.includes("gearbox") && normalized.includes("transmission");
}

function parseNumericFromSpec(spec: any): number | null {
  if (spec?.valueNormalized != null && spec.valueNormalized !== "") {
    const n = Number(spec.valueNormalized);
    if (!Number.isNaN(n)) return n;
  }
  if (spec?.value != null && String(spec.value).trim() !== "") {
    const match = String(spec.value).match(/[\d.]+/);
    if (match) {
      const n = Number(match[0]);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

/** Min/max per spec key from active variants (ModelPage variant list). */
export function buildSpecsRangeFromVariants(
  variants: any[] | undefined | null,
): ISpecRange[] {
  const byKey = new Map<
    string,
    { label: string; unit: string; nums: number[] }
  >();

  for (const variant of variants ?? []) {
    if (variant?.status === "discontinued") continue;
    for (const section of variant?.specSections ?? []) {
      for (const spec of section?.specs ?? []) {
        const key = spec?.key?.trim();
        const keyLower = key.toLowerCase();
        if (!key || (TEXT_SPEC_KEYS.has(keyLower) && keyLower !== "gearbox")) continue;

        const num = parseNumericFromSpec(spec);
        if (num == null) continue;

        const entry = byKey.get(key) ?? {
          label: spec.label || key,
          unit: spec.unit ?? "",
          nums: [] as number[],
        };
        entry.nums.push(num);
        if (spec.label) entry.label = spec.label;
        if (spec.unit) entry.unit = spec.unit;
        byKey.set(key, entry);
      }
    }
  }

  return Array.from(byKey.entries()).map(([key, { label, unit, nums }]) => ({
    key,
    label,
    unit,
    min: Math.min(...nums),
    max: Math.max(...nums),
  }));
}

/** API `specs_range` wins per key; variant aggregation fills gaps. */
export function mergeSpecsRanges(
  fromModel: ISpecRange[] | undefined | null,
  fromVariants: ISpecRange[],
): ISpecRange[] {
  const map = new Map<string, ISpecRange>();
  for (const item of fromVariants) {
    map.set(item.key.trim().toLowerCase(), item);
  }
  for (const item of fromModel ?? []) {
    map.set(item.key.trim().toLowerCase(), item);
  }
  return Array.from(map.values());
}

// engine_type/motor_type are text spec items keyed inside specSections (set
// per-variant in the CMS), not top-level model fields — there is no
// model.engine_types/motor_types array anywhere in the pipeline.
export function getSpecValueFromSections(sections: any[], key: string): string {
  if (!Array.isArray(sections)) return "";
  for (const section of sections) {
    for (const spec of section?.specs || []) {
      if (spec?.key?.trim().toLowerCase() !== key) continue;
      let value: any = null;
      if (spec.value !== undefined && spec.value !== null && String(spec.value).trim() !== "") {
        value = spec.value;
      } else if (spec.valueNormalized !== undefined && spec.valueNormalized !== null) {
        value = spec.unit ? `${spec.valueNormalized} ${spec.unit}` : String(spec.valueNormalized);
      } else {
        value = spec.defaultValue ?? spec.display ?? "";
      }
      return value === null || value === undefined ? "" : String(value).trim();
    }
  }
  return "";
}

const EV_KEY_SPECS = [
  { key: RANGE_SPEC_KEY, label: "Range" },
  { key: "battery_capacity", label: "Battery Capacity" },
  { key: "max_power", label: "Max Power" },
  { key: "max_torque", label: "Max Torque" },
  { key: "charging_time", label: "Charging Time" },
  { key: "seating_capacity", label: "Seating Capacity" },
];
const CHARGING_TIME_KEYS = ["charging_time", "charging_time_dc", "charging_time_ac"]; // specs_range charging_time has no unit, so prefer the variant's text value

export function isElectricOnlyModel(model: any): boolean {
  const fuelTypes: string[] = Array.isArray(model?.fuel_types) ? model.fuel_types : [];
  return fuelTypes.length > 0 && fuelTypes.every((f) => String(f).toLowerCase() === "electric"); // mixed-powertrain models keep the standard grid
}

export function getEvKeySpecs(model: any): { label: string; value: string }[] {
  const baseSections = model?.base_variant?.specSections || model?.specSections || [];
  return EV_KEY_SPECS.map(({ key, label }) => ({
    label,
    value: key === "charging_time"
      ? CHARGING_TIME_KEYS.map((k) => getSpecValueFromSections(baseSections, k)).find(Boolean) || ""
      : formatSpecsRangeValue(model?.specs_range, key) || getSpecValueFromSections(baseSections, key),
  })).filter((s) => s.value);
}
